import { Button, ButtonGroup, Select } from '@tunasong/ui-lib'
import type { KeyNameType } from '@tunasong/music-lib'
import { getKeyNames, prettifyNote, scaleNoteNames } from '@tunasong/music-lib'
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import { Typography, useDimensions } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import { KeySelect } from './key-select.js'
import styles from './scale-select.module.css'

export interface ScaleSelectProps {
  className?: string
  value?: Partial<Scale> | null
  title?: string
  fullWidth?: boolean
  variant?: KeyNameType
  allowNone?: boolean
  /**
   * `grid` is the key control (key selector #2764, spec docs/ux/2026-10-24-key-selector.md): read-back, spelled
   * roots, Major | Minor | More…. `row` is the original wrapping button row, kept for the pages #2766 moves over.
   */
  layout?: 'row' | 'grid'
  /** Grid layout: `view` opens "More scales" in place, for the phone drawer */
  moreAs?: 'menu' | 'view'
  /** Grid layout: the phone view's back button, named for where it returns */
  moreBackLabel?: string
  onChange(scale: Scale | null): void
}

export const ScaleSelect: FC<ScaleSelectProps> = props => {
  const {
    value = null,
    allowNone = true,
    onChange,
    variant = 'common',
    layout = 'row',
    title,
    moreAs,
    moreBackLabel,
    className,
  } = props

  const { isSmallOrSmaller } = useDimensions()

  if (layout === 'grid') {
    return (
      <KeySelect
        className={className}
        value={value}
        title={title}
        variant={variant}
        allowNone={allowNone}
        moreAs={moreAs}
        moreBackLabel={moreBackLabel}
        onChange={onChange}
      />
    )
  }

  const handleScaleType = (type: string | null) => {
    const scale = type ? { root: value?.root ?? 'C', type: type as ScaleType } : null
    onChange(scale)
  }

  const handleRoot = (note?: NoteName) => {
    onChange(note ? { root: note, type: value?.type ?? 'major' } : null)
  }

  const keyNames = getKeyNames(variant)

  const showModes = variant === 'all' || variant === 'mode'

  return (
    <div
      className={cn(styles.rootRow, className)}
      style={{
        alignItems: isSmallOrSmaller ? 'flex-start' : 'center',
        flexDirection: isSmallOrSmaller ? 'column' : 'row',
      }}
    >
      <Typography variant="subtitle2" className={styles.rootLabel}>
        {title ?? 'Root'}
      </Typography>
      <ButtonGroup
        variant="outlined"
        size="small"
        className={styles.buttonGroupWrap}
        aria-label={title ?? 'Root'}
        data-layout="row"
      >
        {allowNone ? (
          <Button
            className={styles.noneButton}
            variant={value === null ? 'contained' : 'outlined'}
            color={value === null ? 'primary' : 'inherit'}
            aria-pressed={value === null}
            onClick={() => handleScaleType(null)}
          >
            <i>None</i>
          </Button>
        ) : null}
        {scaleNoteNames.map(note => (
          <Button
            className={styles.noteButton}
            key={note}
            variant={value?.root === note ? 'contained' : 'outlined'}
            color={value?.root === note ? 'primary' : 'inherit'}
            aria-pressed={value?.root === note}
            onClick={() => handleRoot(note)}
          >
            {prettifyNote(note)}
          </Button>
        ))}
      </ButtonGroup>
      <div className={styles.scaleTypeSelect} style={{ marginBottom: showModes ? 16 : 0 }}>
        <Select
          label="Scale Type"
          value={value?.type ?? ''}
          onChange={e => handleScaleType(e.target.value || null)}
          disabled={value === null}
        >
          <option value="">Select scale type...</option>
          {keyNames.map(name => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
      </div>
    </div>
  )
}
